import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  RefreshCw,
  Maximize2,
  Minimize2,
  ExternalLink,
  ChevronRight,
  ShieldAlert,
  Bot,
  User,
} from "lucide-react";
import axios from "axios";
import "./AIChat.css";

interface RecommendationItem {
  id: string;
  title: string;
  type: "class" | "treatment" | "practitioner" | "package";
  category?: string;
  price?: string | number;
  location?: string;
  url: string;
  image?: string;
}

interface ChatMessage {
  id: string;
  sender: "user" | "assistant";
  text: string;
  recommendations?: RecommendationItem[];
  timestamp: number;
}

const STORAGE_KEY = "metafit_ai_chat_history";
const LANG_STORAGE_KEY = "metafit_ai_chat_lang";

const QUICK_SUGGESTIONS = [
  { label: "🧘 Yoga Classes", query: "Can you recommend popular yoga classes and their timings?" },
  { label: "🌿 Ayurvedic Doctor", query: "I want to consult an Ayurvedic doctor for holistic health." },
  { label: "💆 Stress & Anxiety Help", query: "What wellness therapies or sound healing can help with stress and better sleep?" },
  { label: "🌸 Detox & Retreats", query: "Tell me about luxury wellness retreats and naturopathy packages." },
  { label: "📅 How to Book", query: "How does the booking process work on MetaFit?" },
  { label: "🇮🇳 हिन्दी में पूछें", query: "नमस्ते, MetaFit पर कौन-कौन सी वेलनेस सेवाएँ और योगा क्लासेस उपलब्ध हैं?" },
];

export const AIChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("auto");
  const [inputMessage, setInputMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasUnread, setHasUnread] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

  // Initial welcome message
  const getWelcomeMessage = (lang: string): ChatMessage => {
    const isHi = lang === "hi";
    return {
      id: "welcome-msg",
      sender: "assistant",
      text: isHi
        ? `नमस्ते! 🙏 मैं **MetaFit AI वेलनेस असिस्टेंट** हूँ।\n\nमैं आपकी योगा क्लासेस, आयुर्वेदिक कंसल्टेशन, डिटॉक्स रिट्रीट्स और होलिस्टिक वेलनेस से जुड़े किसी भी सवाल में मदद कर सकता हूँ। आप क्या जानना चाहते हैं?`
        : `Hello & welcome to **MetaFit Wellness**! 🌿\n\nI am your personalized AI Wellness Assistant. I can help you discover live yoga classes, verified Ayurvedic doctors, relaxing retreats, and holistic lifestyle guidance.\n\nHow can I support your wellness journey today?`,
      timestamp: Date.now(),
    };
  };

  // Load chat history from localStorage
  useEffect(() => {
    try {
      const savedLang = localStorage.getItem(LANG_STORAGE_KEY) || "auto";
      setSelectedLanguage(savedLang);

      const savedChat = localStorage.getItem(STORAGE_KEY);
      if (savedChat) {
        const parsed = JSON.parse(savedChat);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          return;
        }
      }
      setMessages([getWelcomeMessage(savedLang)]);
    } catch (e) {
      setMessages([getWelcomeMessage("auto")]);
    }
  }, []);

  // Save chat history to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      } catch (e) {
        // quota exceeded fallback
      }
    }
  }, [messages]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen, messages, isLoading]);

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang);
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  };

  const handleClearChat = () => {
    const fresh = [getWelcomeMessage(selectedLanguage)];
    setMessages(fresh);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  };

  const handleSendMessage = async (customText?: string) => {
    const query = (customText || inputMessage).trim();
    if (!query || isLoading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: query,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Prepare compact history for backend LLM
      const historyPayload = messages.slice(-6).map((m) => ({
        role: m.sender === "user" ? "user" : "assistant",
        content: m.text,
      }));

      interface ChatApiResponse {
        success: boolean;
        reply?: string;
        recommendations?: RecommendationItem[];
        error?: string;
      }

      const response = await axios.post<ChatApiResponse>(
        `${apiBaseUrl}/api/ai/chat`,
        {
          message: query,
          history: historyPayload,
          language: selectedLanguage,
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 25000,
        }
      );

      if (response.data && response.data.success) {
        const assistantMsg: ChatMessage = {
          id: `assistant-${Date.now()}`,
          sender: "assistant",
          text: response.data.reply || "I am here to support your holistic wellness journey with MetaFit.",
          recommendations: response.data.recommendations || [],
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
      } else {
        throw new Error(response.data?.error || "Invalid response");
      }
    } catch (error: any) {
      console.error("[MetaFit AI] Chat request failed:", error);
      const errorMsg: ChatMessage = {
        id: `assistant-err-${Date.now()}`,
        sender: "assistant",
        text: selectedLanguage === "hi"
          ? `क्षमा करें, इस समय संपर्क करने में अस्थायी समस्या आ रही है। कृपया कुछ देर बाद पुनः प्रयास करें या हमारे [कंसल्टेशन पेज](/services) पर सीधे विज़िट करें।`
          : `I am currently experiencing a momentary connectivity issue. Please try asking again in a moment, or explore our verified offerings directly at [MetaFit Services](/services).`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Render rich markdown elements (bold, links, lists) safely
   */
  const renderFormattedText = (rawText: string) => {
    const lines = rawText.split("\n");

    return lines.map((line, lineIndex) => {
      // Check bullet point
      const isBullet = line.trim().startsWith("•") || line.trim().startsWith("-");
      const cleanLine = isBullet ? line.trim().replace(/^[•-]\s*/, "") : line;


      // Parse bolding and markdown links [text](url)
      const parts: React.ReactNode[] = [];
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)|(\*\*[^*]+\*\*)/g;
      let lastIndex = 0;
      let match;

      while ((match = linkRegex.exec(cleanLine)) !== null) {
        if (match.index > lastIndex) {
          parts.push(cleanLine.substring(lastIndex, match.index));
        }

        if (match[1] && match[2]) {
          // It is a link [text](url)
          const linkText = match[1];
          const linkUrl = match[2];
          const isInternal = linkUrl.startsWith("/");

          parts.push(
            isInternal ? (
              <Link
                key={`link-${lineIndex}-${match.index}`}
                to={linkUrl}
                onClick={() => {
                  if (window.innerWidth < 640) setIsOpen(false);
                }}
                className="text-[#0d877d] underline font-semibold hover:text-[#086a62]"
              >
                {linkText}
              </Link>
            ) : (
              <a
                key={`ext-${lineIndex}-${match.index}`}
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0d877d] underline font-semibold hover:text-[#086a62]"
              >
                {linkText}
              </a>
            )
          );
        } else if (match[3]) {
          // It is bold **text**
          const boldText = match[3].slice(2, -2);
          parts.push(<strong key={`b-${lineIndex}-${match.index}`}>{boldText}</strong>);
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < cleanLine.length) {
        parts.push(cleanLine.substring(lastIndex));
      }

      if (isBullet) {
        return (
          <li key={`li-${lineIndex}`} className="mb-1 text-[13px]">
            {parts}
          </li>
        );
      }

      if (!line.trim()) {
        return <div key={`empty-${lineIndex}`} className="h-2" />;
      }

      return (
        <p key={`p-${lineIndex}`} className="mb-1 text-[13px] leading-relaxed">
          {parts}
        </p>
      );
    });
  };

  return (
    <>
      {/* Standalone 3D Doctor Avatar with "Ask AI" Note */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => {
            setIsOpen(true);
            setHasUnread(false);
          }}
          className="metafit-ai-popout-avatar-btn"
          aria-label="Open MetaFit AI Assistant"
          title="Ask MetaFit AI Doctor"
        >
          {/* Note Bubble Tag */}
          <div className="metafit-ai-note-tag">
            <span className="metafit-ai-note-sparkle">✨</span>
            <span className="metafit-ai-note-text">Ask AI</span>
          </div>

          {/* 3D Doctor Avatar */}
          <div className="metafit-ai-avatar-wrapper">
            <img
              src="/images/metafit_ai_doctor_standalone.png"
              alt="MetaFit AI Doctor"
              className="metafit-ai-popout-avatar-img"
            />
            <span className="metafit-ai-standalone-online-dot" />
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`metafit-ai-window ${isExpanded ? "expanded" : ""}`}>
          {/* Header */}
          <div className="metafit-ai-header">
            <div className="metafit-ai-header-brand">
              <div className="metafit-ai-avatar-header">
                <img
                  src="/images/metafit_ai_doctor.jpg"
                  alt="MetaFit AI Doctor"
                  className="w-full h-full object-cover object-top rounded-full"
                />
              </div>
              <div>
                <div className="metafit-ai-header-title">MetaFit AI Assistant</div>
                <div className="metafit-ai-status-badge">
                  <span className="metafit-ai-status-dot" />
                  <span>Online • Holistic Wellness Guide</span>
                </div>
              </div>
            </div>

            <div className="metafit-ai-header-actions">
              <button
                type="button"
                onClick={handleClearChat}
                className="metafit-ai-header-btn"
                title="Restart chat"
                aria-label="Restart chat"
              >
                <RefreshCw size={14} />
              </button>
              <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="metafit-ai-header-btn"
                title={isExpanded ? "Standard size" : "Expand window"}
                aria-label="Toggle chat window size"
              >
                {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="metafit-ai-header-btn"
                title="Close chat"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Language Switcher Bar */}
          <div className="metafit-ai-lang-bar">
            <span>Language / भाषा:</span>
            <div className="metafit-ai-lang-options">
              <button
                type="button"
                onClick={() => handleLanguageChange("auto")}
                className={`metafit-ai-lang-btn ${selectedLanguage === "auto" ? "active" : ""}`}
              >
                Auto
              </button>
              <button
                type="button"
                onClick={() => handleLanguageChange("en")}
                className={`metafit-ai-lang-btn ${selectedLanguage === "en" ? "active" : ""}`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => handleLanguageChange("hi")}
                className={`metafit-ai-lang-btn ${selectedLanguage === "hi" ? "active" : ""}`}
              >
                हिन्दी
              </button>
            </div>
          </div>

          {/* Messages Stream */}
          <div className="metafit-ai-messages">
            {/* MetaFit Intro Banner */}
            <div className="metafit-ai-welcome-box">
              <div className="metafit-ai-welcome-title">
                <Sparkles size={15} />
                <span>Verified Holistic Guidance</span>
              </div>
              <div className="metafit-ai-welcome-desc">
                Discover live yoga classes, consult Ayurvedic doctors, explore wellness retreats, or ask lifestyle questions in English or हिन्दी.
              </div>

              {/* Quick Suggestion Pills */}
              <div className="metafit-ai-suggestions-title">Common Topics</div>
              <div className="metafit-ai-suggestions-grid">
                {QUICK_SUGGESTIONS.map((s, idx) => (
                  <button
                    key={`sug-${idx}`}
                    type="button"
                    onClick={() => handleSendMessage(s.query)}
                    className="metafit-ai-chip"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Conversation Messages */}
            {messages.map((msg) => (
              <div key={msg.id} className={`metafit-ai-msg-row ${msg.sender}`}>
                <div className={`metafit-ai-msg-avatar ${msg.sender}`}>
                  {msg.sender === "assistant" ? (
                    <img
                      src="/images/metafit_ai_doctor.jpg"
                      alt="Doctor"
                      className="w-full h-full object-cover object-top rounded-full"
                    />
                  ) : (
                    <User size={15} />
                  )}
                </div>

                <div className="flex flex-col max-w-[85%]">
                  <div className="metafit-ai-bubble">{renderFormattedText(msg.text)}</div>

                  {/* Structured Recommendation Cards if present */}
                  {Array.isArray(msg.recommendations) && msg.recommendations.length > 0 && (
                    <div className="metafit-ai-recs-container">
                      {msg.recommendations.map((rec) => (
                        <Link
                          key={rec.id}
                          to={rec.url}
                          onClick={() => {
                            if (window.innerWidth < 640) setIsOpen(false);
                          }}
                          className="metafit-ai-rec-card"
                        >
                          <div className="metafit-ai-rec-info">
                            <span className="metafit-ai-rec-type-badge">{rec.type}</span>
                            <div className="metafit-ai-rec-title">{rec.title}</div>
                            <div className="metafit-ai-rec-sub">
                              {rec.price && <span className="metafit-ai-rec-price">{rec.price}</span>}
                              {rec.location && <span>• {rec.location}</span>}
                            </div>
                          </div>
                          <div className="metafit-ai-rec-action-btn">
                            <span>View</span>
                            <ChevronRight size={13} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isLoading && (
              <div className="metafit-ai-msg-row assistant">
                <div className="metafit-ai-msg-avatar assistant">
                  <img
                    src="/images/metafit_ai_doctor.jpg"
                    alt="Doctor"
                    className="w-full h-full object-cover object-top rounded-full"
                  />
                </div>
                <div className="metafit-ai-typing">
                  <div className="metafit-ai-dot" />
                  <div className="metafit-ai-dot" />
                  <div className="metafit-ai-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Safety Disclaimer */}
          <div className="metafit-ai-disclaimer">
            🌿 MetaFit AI provides general wellness guidance, not medical diagnosis or prescriptions. For medical emergencies, consult a qualified physician.
          </div>

          {/* Input Box */}
          <div className="metafit-ai-input-box">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                selectedLanguage === "hi"
                  ? "योगा, डॉक्टर, डिटॉक्स या वेलनेस टिप्स के बारे में पूछें..."
                  : "Ask about yoga, doctors, retreats, or health tips..."
              }
              className="metafit-ai-input"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => handleSendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="metafit-ai-send-btn"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChat;
